import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Link2 } from 'lucide-react'
import {
  apiCreateCampaign, apiUpdateCampaign, apiListTemplates,
  Campaign, CreateCampaignPayload, FormTemplate, TemplateSection,
} from '../lib/api'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog'
import { CatalogSupplierPicker } from './CatalogSupplierPicker'

// ─── Props ────────────────────────────────────────────────────────────────────

type CampaignWizardProps = {
  onSave: (campaign: Campaign) => void
  onCancel: () => void
  initialCampaign?: Campaign
}

// ─── Utilitaire ───────────────────────────────────────────────────────────────

function countTemplateFields(sections: unknown): { required: number; optional: number } {
  const parsed = sections as TemplateSection[]
  if (!Array.isArray(parsed)) return { required: 0, optional: 0 }
  return parsed.reduce(
    (acc, section) => {
      section.fields.forEach((f) => {
        if (f.required) acc.required++
        else acc.optional++
      })
      return acc
    },
    { required: 0, optional: 0 }
  )
}

// ─── Composant ────────────────────────────────────────────────────────────────

export function CampaignWizard({ onSave, onCancel, initialCampaign }: CampaignWizardProps) {
  const isEditing = !!initialCampaign
  const [name, setName] = useState(initialCampaign?.name ?? '')
  const [description, setDescription] = useState(initialCampaign?.description ?? '')
  const [startsAt, setStartsAt] = useState(
    initialCampaign?.startsAt ? initialCampaign.startsAt.slice(0, 10) : ''
  )
  const [dueAt, setDueAt] = useState(
    initialCampaign?.dueAt ? initialCampaign.dueAt.slice(0, 10) : ''
  )
  const [formTemplateId, setFormTemplateId] = useState<string | null>(initialCampaign?.formTemplateId ?? null)
  const [templates, setTemplates] = useState<FormTemplate[]>([])
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [templateLoadError, setTemplateLoadError] = useState(false)
  // Story 3.5 — étape « Fournisseurs » optionnelle, uniquement à la création.
  // Mode édition (`isEditing`) : les assignations passent par
  // `CampaignSupplierDialog`, l'étape est masquée.
  const [supplierIds, setSupplierIds] = useState<string[]>([])

  const loadTemplates = () => {
    setIsLoadingTemplates(true)
    setTemplateLoadError(false)
    apiListTemplates()
      .then(setTemplates)
      .catch(() => {
        toast.error('Erreur lors du chargement des templates', { duration: Infinity })
        setTemplateLoadError(true)
      })
      .finally(() => setIsLoadingTemplates(false))
  }

  useEffect(() => { loadTemplates() }, [])

  const handleNameBlur = () => {
    if (name.trim() === '') {
      setErrors((prev) => ({ ...prev, name: 'Le titre de la campagne est requis' }))
    } else {
      setErrors((prev) => { const next = { ...prev }; delete next.name; return next })
    }
  }

  const handleDueAtBlur = () => {
    if (startsAt && dueAt && new Date(dueAt) <= new Date(startsAt)) {
      setErrors((prev) => ({
        ...prev,
        dueAt: 'La date de fin doit être postérieure à la date de début',
      }))
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n.dueAt; return n })
    }
  }

  const handleSubmit = async () => {
    if (name.trim() === '') {
      setErrors((prev) => ({ ...prev, name: 'Le titre de la campagne est requis' }))
      return
    }
    if (startsAt && dueAt && new Date(dueAt) <= new Date(startsAt)) {
      setErrors((prev) => ({
        ...prev,
        dueAt: 'La date de fin doit être postérieure à la date de début',
      }))
      return
    }
    setIsSubmitting(true)
    try {
      const payload: CreateCampaignPayload = {
        name: name.trim(),
        description: description.trim() || null,
        // Convertir "YYYY-MM-DD" (input type=date) en ISO 8601 complet attendu par le backend
        startsAt: startsAt ? `${startsAt}T00:00:00.000Z` : null,
        dueAt: dueAt ? `${dueAt}T00:00:00.000Z` : null,
        formTemplateId: formTemplateId,
        // Story 3.5 — pré-assignation depuis catalogue. Mode édition : on
        // n'envoie pas `supplierIds` (PATCH ignorerait silencieusement, mais
        // mieux vaut être explicite côté payload pour qu'un futur lecteur le voie).
        ...(isEditing ? {} : { supplierIds: supplierIds.length > 0 ? supplierIds : undefined }),
      }
      let campaign: Campaign
      if (isEditing && initialCampaign) {
        campaign = await apiUpdateCampaign(initialCampaign.id, payload)
        toast.success('Campagne mise à jour', { duration: 4000 })
      } else {
        const created = await apiCreateCampaign(payload)
        campaign = created
        const assignment = created.assignment
        if (assignment && assignment.added > 0 && assignment.skipped.length === 0) {
          toast.success(
            `Campagne créée avec ${assignment.added} fournisseur${assignment.added > 1 ? 's' : ''} ajouté${assignment.added > 1 ? 's' : ''}`,
            { duration: 4000 }
          )
        } else if (assignment && assignment.skipped.length > 0) {
          toast.warning(
            `Campagne créée — ${assignment.added} fournisseur${assignment.added !== 1 ? 's' : ''} ajouté${assignment.added !== 1 ? 's' : ''}, ${assignment.skipped.length} ignoré${assignment.skipped.length > 1 ? 's' : ''}.`,
            { duration: 6000 }
          )
        } else {
          toast.success('Campagne créée', { duration: 4000 })
        }
      }
      onSave(campaign)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : isEditing ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création de la campagne'
      toast.error(msg, { duration: Infinity })
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedTemplate = templates.find((t) => t.id === formTemplateId)
  const fieldCount = selectedTemplate ? countTemplateFields(selectedTemplate.sections) : null

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !isSubmitting) onCancel() }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Modifier la campagne' : 'Nouvelle campagne'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Nom */}
          <div className="space-y-1.5">
            <Label htmlFor="cw-name">
              Titre de la campagne <span className="text-red-500">*</span>
            </Label>
            <Input
              id="cw-name"
              value={name}
              onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => { const n = { ...p }; delete n.name; return n }) }}
              onBlur={handleNameBlur}
              placeholder="Ex : EPD Acier 2026"
            />
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="cw-description">Description (optionnelle)</Label>
            <Textarea
              id="cw-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Objectifs et périmètre de la campagne..."
              rows={2}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="cw-starts">Date de début</Label>
              <Input
                id="cw-starts"
                type="date"
                value={startsAt}
                onChange={(e) => {
                  setStartsAt(e.target.value)
                  if (errors.dueAt) setErrors((p) => { const n = { ...p }; delete n.dueAt; return n })
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cw-due">Date de fin</Label>
              <Input
                id="cw-due"
                type="date"
                value={dueAt}
                onChange={(e) => {
                  setDueAt(e.target.value)
                  if (errors.dueAt) setErrors((p) => { const n = { ...p }; delete n.dueAt; return n })
                }}
                onBlur={handleDueAtBlur}
              />
              {errors.dueAt && <p className="text-sm text-red-600">{errors.dueAt}</p>}
            </div>
          </div>

          {/* Template */}
          <div className="space-y-1.5">
            <Label htmlFor="cw-template">Template de formulaire</Label>
            <Select
              value={formTemplateId ?? ''}
              onValueChange={(v) => setFormTemplateId(v || null)}
              disabled={isLoadingTemplates}
            >
              <SelectTrigger id="cw-template">
                <SelectValue placeholder={isLoadingTemplates ? 'Chargement...' : 'Sélectionner un template'} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}{t.isSystem ? ' ★' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {templateLoadError && (
              <p className="text-sm text-red-600 flex items-center gap-2">
                Impossible de charger les templates.{' '}
                <button type="button" className="underline" onClick={loadTemplates}>Réessayer</button>
              </p>
            )}
            {selectedTemplate && fieldCount && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{selectedTemplate.name}</span> —{' '}
                {fieldCount.required} champ{fieldCount.required !== 1 ? 's' : ''} obligatoire{fieldCount.required !== 1 ? 's' : ''},{' '}
                {fieldCount.optional} optionnel{fieldCount.optional !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Story 3.5 — étape Fournisseurs (création uniquement, skippable) */}
          {!isEditing && (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <Link2 className="h-4 w-4" aria-hidden />
                Fournisseurs depuis votre catalogue
                <span className="text-xs text-muted-foreground font-normal">(optionnel)</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                Sélectionnez des fournisseurs à pré-assigner. Vous pourrez en ajouter d'autres par la suite.
              </p>
              <CatalogSupplierPicker
                initialSelectedIds={supplierIds}
                onChange={setSupplierIds}
              />
            </div>
          )}

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? (isEditing ? 'Enregistrement...' : 'Création...')
                : (isEditing ? 'Enregistrer les modifications' : 'Créer la campagne')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
